import { IRawAccountStatus } from 'models/account.model'
import { IRequestOptions, IResponseResult } from 'models/api.model'
import { TAnswers } from 'models/common.model'
import {
  IBindUserResponseRaw,
  IGetUserStatusResponseRaw,
  TUserConfig,
} from 'models/user.model'

import { ApiService } from 'services/api.service'

import {
  APP_NAME,
  RequestMethod,
  TEST_ENV_QUERY_PARAM,
} from 'root-constants/common'

export class UserApi {
  private readonly baseUrlV1 = 'v1/flow'
  private readonly baseUrlV2 = 'v2/leads'
  private readonly baseUrlV3 = 'v2/reteno'
  private api: ApiService

  constructor(api: ApiService) {
    this.api = api
  }

  bindUser({
    token,
    uuid,
  }: {
    token: string
    uuid: string
  }): Promise<IResponseResult<IBindUserResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        token,
      },
    }

    return this.api.makeRequest<IBindUserResponseRaw>(
      `${this.baseUrlV2}/${uuid}/${APP_NAME}/signup`,
      options,
    )
  }

  getUserStatus(
    uuid: string,
  ): Promise<IResponseResult<IGetUserStatusResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.GET,
    }

    return this.api.makeRequest<IGetUserStatusResponseRaw>(
      `${this.baseUrlV2}/${uuid}/${APP_NAME}`,
      options,
    )
  }

  getUUID({
    cohort,
    appId,
    locale,
    promoCode = '',
    utm,
  }: {
    cohort: string
    appId: string
    locale: string
    promoCode?: string
    utm: Record<string, string>
  }): Promise<IResponseResult<string>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        cohort,
        locale,
        app_id: appId,
        app_name: APP_NAME,
        utm,
        promo_code: promoCode,
        is_test_environment:
          document.location.search.includes(TEST_ENV_QUERY_PARAM),
      },
    }

    return this.api.makeRequest<string>(this.baseUrlV2, options)
  }

  saveFacebookParams({
    fbc,
    fbp,
    uuid,
  }: {
    uuid: string
    fbc: string
    fbp: string
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        fbc,
        fbp,
      },
    }

    return this.api.makeRequest<never>(
      `${this.baseUrlV2}/${uuid}/${APP_NAME}/facebook`,
      options,
    )
  }

  saveGoogleAdsParams({
    uuid,
    gclid,
    gaid,
  }: {
    uuid: string
    gclid: string
    gaid: string
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      params: { ctid: gclid, gaid },
    }

    return this.api.makeRequest<never>(
      `${this.baseUrlV2}/${uuid}/google`,
      options,
    )
  }

  saveUserAnswers({
    uuid,
    answers,
    isFinished = false,
    isSkipped = false,
  }: {
    uuid: string
    answers: TAnswers
    isFinished?: boolean
    isSkipped?: boolean
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        is_finished: isFinished,
        is_skipped: isSkipped,
        quiz: answers,
        version: '2.0',
      },
    }

    return this.api.makeRequest<never>(
      `${this.baseUrlV2}/${uuid}/${APP_NAME}/quiz`,
      options,
    )
  }

  saveUserConfig({
    uuid,
    config,
  }: {
    uuid: string
    config: TUserConfig
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        config,
      },
    }

    return this.api.makeRequest<never>(
      `${this.baseUrlV2}/${uuid}/${APP_NAME}/config`,
      options,
    )
  }

  saveUserEmail({
    uuid,
    email,
  }: {
    uuid: string
    email: string
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        email: email.toLowerCase(),
      },
    }

    return this.api.makeRequest<never>(
      `${this.baseUrlV2}/${uuid}/${APP_NAME}/email`,
      options,
    )
  }

  savePlanAdditions({
    uuid,
    planAdditions,
  }: {
    uuid: string
    planAdditions: string[]
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: { addons: planAdditions },
    }

    return this.api.makeRequest<never>(
      `${this.baseUrlV2}/${uuid}/${APP_NAME}/addons`,
      options,
    )
  }

  getAutologinToken(
    uuid: string,
  ): Promise<IResponseResult<{ status: boolean; custom_token: string }>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        uuid,
        app_name: APP_NAME,
      },
    }

    return this.api.makeRequest<{ status: boolean; custom_token: string }>(
      `${this.baseUrlV1}/login`,
      options,
    )
  }

  getUserPredictedLtv(uuid: string): Promise<
    IResponseResult<{
      status: boolean
      predicted_ltv: number
      currency: string
    }>
  > {
    const options: IRequestOptions = {
      method: RequestMethod.GET,
    }

    return this.api.makeRequest<{ status: boolean; predicted_ltv: number }>(
      `${this.baseUrlV2}/${uuid}/${APP_NAME}/predicted_ltv`,
      options,
    )
  }

  saveUserSCCID({
    sccid,
    scid,
    uuid,
  }: {
    sccid: string
    scid: string
    uuid: string
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      params: { sccid, uuid_c1: scid },
    }

    return this.api.makeRequest(`${this.baseUrlV2}/${uuid}/snapchat`, options)
  }

  getAccountStatus(email: string): Promise<IResponseResult<IRawAccountStatus>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        app_name: APP_NAME,
        email,
      },
    }

    return this.api.makeRequest<IRawAccountStatus>(
      `${this.baseUrlV2}/account/status`,
      options,
    )
  }

  sendRetenoEvent({
    uuid,
    retenoEvent,
  }: {
    uuid: string
    retenoEvent: string
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        app_name: APP_NAME,
        type: retenoEvent,
        uuid,
      },
    }

    return this.api.makeRequest<IGetUserStatusResponseRaw>(
      `${this.baseUrlV3}/event`,
      options,
    )
  }
}
